import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Play, Volume2, VolumeX, Pause, SkipForward } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useHeroMusic } from '../context/HeroMusicContext';
import { translations, companyInfo } from '../data/mockData';
import { getApiBaseUrl } from '../utils/urlHelper';
import { GOLD, NAVY, CREAM, alpha } from '../theme/palette';

const API = getApiBaseUrl();
const DEFAULT_FALLBACK =
  'https://static.prod-images.emergentagent.com/jobs/2fffef19-c046-4903-bd11-ec83a00b3ace/images/c4b9e4d8248e5c26b32f24aa778d4f1c830de603d78127c65d01364c38da4da9.png';

// Belmond Cathedral theme tokens
const SERIF = { fontFamily: '"Fraunces", serif' };

// Iter 320bv — Hero Carousel (unchanged logic): reads `hero.carousel_enabled` +
// `hero.slides[]` from homepage settings. Cross-fades 800ms, each slide honors
// its own `duration_secs`. Autoplay pauses on hover.
// Belmond redesign: cinematic black overlay, serif title, gold accents, large
// isolated logo. Keeps full admin control over slides and copy.
const Hero = () => {
  const { language } = useLanguage();
  const music = useHeroMusic();
  const t = translations[language].hero;
  const [heroSettings, setHeroSettings] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/homepage-settings`).then(r => r.json()).then(d => setHeroSettings(d?.hero)).catch(() => {});
  }, []);

  const h = heroSettings || {};
  // Iter 330 — slides agora podem ter type: 'video' | 'image' (default image).
  // Vídeos tocam em loop mudo; imagens respeitam duration_secs.
  const rawSlides = Array.isArray(h.slides) ? h.slides.filter(s => s && (s.image_url || s.video_url)) : [];
  const carouselOn = !!h.carousel_enabled && rawSlides.length >= 1;
  const slides = carouselOn ? rawSlides : [{
    image_url: h.background_image || DEFAULT_FALLBACK,
    duration_secs: 7, focus: 'center', type: 'image',
  }];

  const resolveSrc = (u) => (!u ? DEFAULT_FALLBACK : (u.startsWith('http') ? u : `${API}${u}`));

  useEffect(() => {
    if (!carouselOn || paused || slides.length < 2) return;
    const cur = slides[currentSlide] || {};
    // For videos we also use duration_secs (admin-controlled fade-out time).
    const dur = Math.max(2, Number(cur.duration_secs) || 7) * 1000;
    timerRef.current = setTimeout(
      () => setCurrentSlide(i => (i + 1) % slides.length), dur);
    return () => clearTimeout(timerRef.current);
  }, [currentSlide, paused, carouselOn, slides.length]);

  const welcome = h[`welcome_${language}`] || t.welcome;
  const subtitle = h[`subtitle_${language}`] || t.subtitle;
  const description = h[`description_${language}`] || t.description;
  const tagline = h[`tagline_${language}`] || t.tagline;
  const cta = h[`cta_${language}`] || t.cta;

  const whatsappNum = language === 'pt' ? companyInfo.whatsapp : companyInfo.whatsappUS;

  return (
    <div
      className="relative min-h-[90vh] flex items-end overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="hero-section"
    >
      {/* Background layers — one per slide (image or video). Only the current is fully opaque */}
      <div className="absolute inset-0 z-0">
        {/* Iter 355 — Pre-compute the first image slide so we can use it as a
            poster/fallback behind any video slide (helps while the mp4 is
            downloading and in the rare case the browser can't decode it). */}
        {(() => { return null; })()}
        {slides.map((s, i) => {
          const isVideo = (s.type === 'video') || (!!s.video_url && !s.image_url);
          const commonStyle = {
            opacity: i === currentSlide ? 1 : 0,
            objectPosition: s.focus === 'top' ? 'center top' : s.focus === 'bottom' ? 'center bottom' : 'center center',
          };
          const fallbackPoster = slides.find(x => x !== s && x.image_url)?.image_url || h.background_image || DEFAULT_FALLBACK;
          return isVideo ? (
            <video
              key={i}
              src={resolveSrc(s.video_url || s.image_url)}
              poster={resolveSrc(s.poster_url || fallbackPoster)}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1600ms] ease-out"
              style={{ ...commonStyle, zIndex: i === currentSlide ? 2 : 1, background: NAVY }}
              data-testid={`hero-slide-${i}`}
              onError={(e) => {
                // If the mp4 fails to load/decode, keep the poster frame
                // visible by stripping the broken src.
                try { e.currentTarget.removeAttribute('src'); e.currentTarget.load(); } catch {}
              }}
            />
          ) : (
            <img
              key={i}
              src={resolveSrc(s.image_url)}
              alt={`Slide ${i + 1}`}
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1600ms] ease-out"
              style={{ ...commonStyle, zIndex: i === currentSlide ? 2 : 1 }}
              data-testid={`hero-slide-${i}`}
            />
          );
        })}
        {/* Cinematic gradient overlay — Belmond style */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, color-mix(in srgb, var(--site-navy) 95%, transparent) 0%, color-mix(in srgb, var(--site-navy) 35%, transparent) 50%, color-mix(in srgb, var(--site-navy) 55%, transparent) 100%)' }} />
      </div>

      {/* Content — bottom-left aligned */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pb-20 lg:pb-28 pt-32" style={{ color: CREAM }}>
        <div className="max-w-4xl" data-section-head>
          <div className="flex items-center gap-3 mb-7 text-[11px] tracking-[0.4em] uppercase" style={{ color: GOLD }} data-font="hero-text">
            <span className="w-10 h-px" style={{ background: GOLD }} />
            {welcome}
          </div>
          <h1 style={SERIF} className="text-5xl sm:text-7xl lg:text-[6.5rem] font-light leading-[0.95] tracking-tight mb-8" data-testid="hero-title" data-font="hero-title">
            {tagline.split(/\s+(?=[^\s]*$)/)[0]}
            <br/>
            <em className="italic font-normal" style={{ color: GOLD }}>
              {tagline.split(/\s+(?=[^\s]*$)/)[1] || ''}
            </em>
          </h1>
          <p className="text-base lg:text-lg text-white/80 max-w-2xl mb-4 leading-relaxed" style={{ fontFamily: '"Inter", sans-serif' }} data-font="hero-text">
            {subtitle}
          </p>
          <p className="text-sm lg:text-base text-white/60 max-w-2xl mb-10 leading-relaxed italic" style={{ fontFamily: '"Inter", sans-serif' }} data-font="hero-text">
            {description}
          </p>
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 mb-14">
            <button
              onClick={() => document.getElementById('pilgrimages')?.scrollIntoView({ behavior: 'smooth' })}
              className="group inline-flex items-center gap-4 px-9 py-5 text-[11px] tracking-[0.35em] uppercase font-semibold transition hover:opacity-90"
              style={{ background: GOLD, color: NAVY, fontFamily: '"Inter", sans-serif' }}
              data-testid="hero-cta-pilgrimages"
            >
              {cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
            </button>
            {/* Iter 359 — Secondary CTA so visitors who want to see every pilgrimage
                can jump straight to the full catalogue without scrolling. */}
            <a
              href="/pilgrimages"
              className="group inline-flex items-center gap-4 px-9 py-5 text-[11px] tracking-[0.35em] uppercase font-medium text-white border transition hover:bg-white/10"
              style={{ borderColor: `${alpha(GOLD, 'aa')}`, fontFamily: '"Inter", sans-serif' }}
              data-testid="hero-cta-all-pilgrimages"
            >
              {language === 'pt' ? 'Ver todas as peregrinações' : language === 'en' ? 'See all pilgrimages' : 'Ver todas las peregrinaciones'}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
            </a>
            <a
              href={`https://api.whatsapp.com/send?phone=${whatsappNum.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-4 px-9 py-5 text-[11px] tracking-[0.35em] uppercase font-medium text-white border transition hover:bg-white/10"
              style={{ borderColor: `${alpha(GOLD, 'aa')}`, fontFamily: '"Inter", sans-serif' }}
              data-testid="hero-cta-whatsapp"
            >
              <Play className="w-3.5 h-3.5" fill="currentColor" />
              {language === 'pt' ? 'Falar no WhatsApp' : language === 'en' ? 'Chat on WhatsApp' : 'Hablar en WhatsApp'}
            </a>
          </div>
        </div>
      </div>

      {/* Slide dots — only when carousel is on */}
      {carouselOn && slides.length > 1 && (
        <div className="absolute bottom-8 right-8 flex gap-1.5 z-20">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className="h-1 transition-all"
              style={{ width: i === currentSlide ? 32 : 12, background: i === currentSlide ? GOLD : 'rgba(255,255,255,0.4)' }}
              aria-label={`Slide ${i + 1}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>
      )}

      {/* Iter 330 — Music controls (mute/unmute · pause · next). Only
          renders when at least one music track is configured. */}
      {music.enabled && (
        <div className="absolute bottom-8 left-6 lg:left-10 z-20 flex items-center gap-2" data-testid="hero-music-controls">
          <button
            type="button"
            onClick={music.toggleMute}
            className="w-10 h-10 rounded-full flex items-center justify-center transition hover:bg-white/10 border backdrop-blur-sm"
            style={{ borderColor: `${alpha(GOLD, '66')}`, color: '#fff', background: 'color-mix(in srgb, var(--site-navy) 35%, transparent)' }}
            aria-label={music.muted ? 'Unmute' : 'Mute'}
            title={music.muted ? (language === 'pt' ? 'Ativar som' : language === 'en' ? 'Unmute' : 'Activar sonido') : (language === 'pt' ? 'Silenciar' : language === 'en' ? 'Mute' : 'Silenciar')}
            data-testid="hero-music-mute"
          >
            {music.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <button
            type="button"
            onClick={music.togglePause}
            className="w-10 h-10 rounded-full flex items-center justify-center transition hover:bg-white/10 border backdrop-blur-sm"
            style={{ borderColor: `${alpha(GOLD, '66')}`, color: '#fff', background: 'color-mix(in srgb, var(--site-navy) 35%, transparent)' }}
            aria-label={music.paused ? 'Play music' : 'Pause music'}
            data-testid="hero-music-pause"
          >
            {music.paused ? <Play className="w-4 h-4" fill="currentColor" /> : <Pause className="w-4 h-4" />}
          </button>
          {music.tracks.length > 1 && (
            <button
              type="button"
              onClick={music.next}
              className="w-10 h-10 rounded-full flex items-center justify-center transition hover:bg-white/10 border backdrop-blur-sm"
              style={{ borderColor: `${alpha(GOLD, '66')}`, color: '#fff', background: 'color-mix(in srgb, var(--site-navy) 35%, transparent)' }}
              aria-label="Next track"
              data-testid="hero-music-next"
            >
              <SkipForward className="w-4 h-4" />
            </button>
          )}
          {music.currentTrack?.title && !music.muted && (
            <span className="hidden lg:inline-block text-[10px] tracking-[0.25em] uppercase text-white/70 ml-2 truncate max-w-[200px]">
              ♪ {music.currentTrack.title}
            </span>
          )}
        </div>
      )}

    </div>
  );
};

export default Hero;
